import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Gift, ChevronLeft, RefreshCw, Plus } from 'lucide-react';
import { api } from '@/api/apiClient';
import LoadingState from '@/components/shared/LoadingState';
import ErrorState from '@/components/shared/ErrorState';
import EmptyState from '@/components/shared/EmptyState';
import { useOdooAuth } from '@/lib/OdooAuthContext';

const fmt2 = (n) =>
  Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATE_LABEL = { draft: 'مسودة', submitted: 'بانتظار الاعتماد', done: 'تم الصرف', rejected: 'مرفوض' };
const STATE_COLOR = {
  draft: 'bg-gray-100 text-gray-600 border-gray-200',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  done: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const TABS = [
  { key: 'submitted', label: 'قيد الاعتماد' },
  { key: 'all', label: 'الكل' },
  { key: 'done', label: 'تم الصرف' },
  { key: 'rejected', label: 'مرفوض' },
];

export default function Gifts() {
  const navigate = useNavigate();
  const { odooSession } = useOdooAuth();
  const isLeader = odooSession?.role === 'manager' || odooSession?.role === 'supervisor';

  const [tab, setTab] = useState(isLeader ? 'submitted' : 'all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/sellgo_v5_gifts', {
        state: tab === 'all' ? undefined : tab,
        limit: 100,
      });
      if (res?.success) setItems(Array.isArray(res.data) ? res.data : []);
      else setError(res?.error || 'تعذّر تحميل الهدايا');
    } catch (e) {
      setError(e?.message || 'تعذّر تحميل الهدايا');
    }
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-2xl mx-auto pb-24" dir="rtl">
      <div className="px-4 pt-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-primary">
          <Gift className="w-5 h-5" />
          <h1 className="text-lg font-bold">الهدايا</h1>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-muted text-muted-foreground">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 mt-3 flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border ${
              tab === t.key ? 'bg-primary text-white border-primary' : 'bg-card text-muted-foreground border-border hover:bg-muted'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="px-4 mt-3 space-y-2">
        {loading && <LoadingState message="جاري تحميل الهدايا..." />}
        {!loading && error && <ErrorState message={error} onRetry={load} />}
        {!loading && !error && items.length === 0 && (
          <EmptyState icon={Gift} title="لا توجد هدايا"
            description={tab === 'submitted' ? 'لا يوجد هدايا بانتظار الاعتماد.' : 'لم يتم صرف أي هدايا بعد.'} />
        )}
        {!loading && !error && items.map((g) => (
          <motion.button key={g.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            onClick={() => navigate(`/gifts/${g.id}`)}
            className="w-full text-right bg-card border border-border rounded-2xl p-3 hover:border-primary/40 transition-colors flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm text-foreground">{g.name}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATE_COLOR[g.state] || ''}`}>
                  {g.state_label || STATE_LABEL[g.state] || g.state}
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {Array.isArray(g.partner_id) ? g.partner_id[1] : '—'}
                {Array.isArray(g.rep_user_id) && <> • {g.rep_user_id[1]}</>}
              </p>
              {(g.issued_date || g.create_date) && (
                <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                  {new Date((g.issued_date || g.create_date).replace(' ', 'T')).toLocaleString('ar-EG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="font-bold text-sm text-foreground">{fmt2(g.amount_total)}</span>
              <ChevronLeft className="w-4 h-4 text-muted-foreground mt-1" />
            </div>
          </motion.button>
        ))}
      </div>

      {/* FAB: new gift */}
      <button onClick={() => navigate('/gifts/new')}
        className="fixed bottom-5 left-5 z-30 bg-primary text-white rounded-full h-14 w-14 flex items-center justify-center shadow-lg shadow-primary/30 hover:bg-primary/90">
        <Plus className="w-6 h-6" />
      </button>
    </div>
  );
}
